import React, { useState, useEffect, useRef } from 'react';
import { Users, MessageSquare, Check, X, UserPlus, Eye, Send, AtSign, MoreVertical, UserMinus, Shield, ShieldOff, UserX, RotateCcw } from 'lucide-react';
import './Friends.css';
import DirectChat from './DirectChat';
import AnonymousChat from './AnonymousChat';
import UserProfile from './UserProfile';
import MyLink from './MyLink';
import { VipName } from './VipName';
import { useChat } from '../context/ChatContext';

const Friends = ({ user, isSubscribed, onRequestSubscription, vipSettings, onUnreadChange, chatWithFriendId, onClearChatWithFriend }) => {
  const { socket } = useChat();
  const userId = user?.id || user?.telegramId || '';

  const friendsCacheKey = `hams_cached_friends_${userId}`;
  const requestsCacheKey = `hams_cached_requests_${userId}`;
  const blockedCacheKey = `hams_cached_blocked_${userId}`;
  const removedCacheKey = `hams_cached_removed_${userId}`;

  const [friends, setFriends] = useState(() => {
    try {
      if (!userId) return [];
      const cached = localStorage.getItem(friendsCacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [requests, setRequests] = useState(() => {
    try {
      if (!userId) return [];
      const cached = localStorage.getItem(requestsCacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [blockedList, setBlockedList] = useState(() => {
    try {
      if (!userId) return [];
      const cached = localStorage.getItem(blockedCacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [removedList, setRemovedList] = useState(() => {
    try {
      if (!userId) return [];
      const cached = localStorage.getItem(removedCacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    if (!userId) return true;
    const cachedFriends = localStorage.getItem(friendsCacheKey);
    return !cachedFriends;
  });

  const [view, setView] = useState('friends'); // 'friends', 'anonymous', 'mylink', 'blocked', 'removed'
  const [directChatFriend, setDirectChatFriend] = useState(null);
  const [viewProfileUserId, setViewProfileUserId] = useState(null);
  const [activeMenuFriendId, setActiveMenuFriendId] = useState(null);
  const [friendsSubView, setFriendsSubView] = useState('list'); // 'list', 'blocked', 'removed'
  const menuRef = useRef(null);

  // Sync to localStorage effects
  useEffect(() => {
    if (userId) {
      localStorage.setItem(friendsCacheKey, JSON.stringify(friends));
    }
  }, [friends, friendsCacheKey, userId]);

  useEffect(() => {
    if (userId) {
      localStorage.setItem(requestsCacheKey, JSON.stringify(requests));
    }
  }, [requests, requestsCacheKey, userId]);

  useEffect(() => {
    if (userId) {
      localStorage.setItem(blockedCacheKey, JSON.stringify(blockedList));
    }
  }, [blockedList, blockedCacheKey, userId]);

  useEffect(() => {
    if (userId) {
      localStorage.setItem(removedCacheKey, JSON.stringify(removedList));
    }
  }, [removedList, removedCacheKey, userId]);

  const activeChatFriendRef = React.useRef(null);

  React.useEffect(() => {
    activeChatFriendRef.current = directChatFriend;
  }, [directChatFriend]);

  React.useEffect(() => {
    if (onUnreadChange) {
      const total = friends.reduce((sum, f) => sum + (f.unreadCount || 0), 0);
      onUnreadChange(total);
    }
  }, [friends, onUnreadChange]);

  const handleOpenChat = (friend) => {
    setFriends(prev => prev.map(f => f.id === friend.id ? { ...f, unreadCount: 0 } : f));
    setDirectChatFriend(friend);
  };

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestSentUserIds, setRequestSentUserIds] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);

  const sortFriendsByActivity = (list) => {
    return [...list].sort((a, b) => {
      const aTs = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTs = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTs - aTs;
    });
  };

  // إغلاق القائمة عند الضغط خارجها
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenuFriendId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // جلب البيانات الأساسية
  useEffect(() => {
    const fetchFriendsAndRequests = async () => {
      if (!user) return;
      const userId = user.id || user.telegramId;
      try {
        const [resFriends, resRequests, resBlocked, resRemoved] = await Promise.all([
          fetch(`/api/friends/${userId}`),
          fetch(`/api/friends/${userId}/requests`),
          fetch(`/api/friends/${userId}/blocked`),
          fetch(`/api/friends/${userId}/removed`)
        ]);
        if (resFriends.ok) {
          const data = await resFriends.json();
          setFriends(sortFriendsByActivity(data.friends.map(f => ({ ...f, isOnline: false, unreadCount: f.unreadCount || 0 }))));
        }
        if (resRequests.ok) {
          const data = await resRequests.json();
          setRequests(data.requests || []);
        }
        if (resBlocked.ok) {
          const data = await resBlocked.json();
          setBlockedList(data.blocked || []);
        }
        if (resRemoved.ok) {
          const data = await resRemoved.json();
          setRemovedList(data.removed || []);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFriendsAndRequests();
  }, [user]);

  // Auto-open direct chat if chatWithFriendId matches any friend
  useEffect(() => {
    if (chatWithFriendId && friends.length > 0) {
      const match = friends.find(f => f.id.toString() === chatWithFriendId.toString());
      if (match) {
        handleOpenChat(match);
        if (onClearChatWithFriend) {
          onClearChatWithFriend();
        }
      }
    }
  }, [chatWithFriendId, friends, onClearChatWithFriend]);

  // إدارة أحداث السوكيت (Socket.io)
  useEffect(() => {
    if (!socket) return;

    const handleInitialOnlineFriends = (onlineFriendIds) => {
        setFriends(prev => prev.map(f => ({
            ...f,
            isOnline: onlineFriendIds.includes(f.id) || f.isOnline
        })));
    };

    const handleFriendOnline = ({ userId }) => {
      setFriends(prev => prev.map(f => f.id === userId ? { ...f, isOnline: true } : f));
    };

    const handleFriendOffline = ({ userId, lastSeen }) => {
      setFriends(prev => prev.map(f => f.id === userId ? { ...f, isOnline: false, lastSeen } : f));
    };

    const handleNewFriend = () => {
        if (user) {
            const userId = user.id || user.telegramId;
            fetch(`/api/friends/${userId}`)
            .then(res => res.json())
            .then(data => {
                setFriends(prev => {
                    const onlineStatusMap = {};
                    prev.forEach(p => onlineStatusMap[p.id] = p.isOnline);
                    return sortFriendsByActivity(data.friends.map(f => ({
                        ...f,
                        isOnline: onlineStatusMap[f.id] || false
                    })));
                });
            });
        }
    };

    const handleFriendRequestReceived = () => {
         if (user) {
            const userId = user.id || user.telegramId;
            fetch(`/api/friends/${userId}/requests`)
            .then(res => res.json())
            .then(data => {
               setRequests(data.requests || []);
            });
         }
    };

    const handleDirectChatNotification = ({ senderId, timestamp }) => {
        const activeFriend = activeChatFriendRef.current;
        if (activeFriend && activeFriend.id === senderId) {
            return; // Already in chat
        }
        setFriends(prev => prev.map(f => {
            if (f.id === senderId) {
                return { ...f, unreadCount: (f.unreadCount || 0) + 1, lastMessageAt: timestamp || new Date().toISOString() };
            }
            return f;
        }).sort((a, b) => {
            const aTs = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
            const bTs = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
            return bTs - aTs;
        }));
    };

    socket.on('initial_online_friends', handleInitialOnlineFriends);
    socket.on('friend_online', handleFriendOnline);
    socket.on('friend_offline', handleFriendOffline);
    socket.on('new_friend_added', handleNewFriend);
    socket.on('friend_request_received', handleFriendRequestReceived);
    socket.on('direct_chat_notification', handleDirectChatNotification);

    return () => {
      socket.off('initial_online_friends', handleInitialOnlineFriends);
      socket.off('friend_online', handleFriendOnline);
      socket.off('friend_offline', handleFriendOffline);
      socket.off('new_friend_added', handleNewFriend);
      socket.off('friend_request_received', handleFriendRequestReceived);
      socket.off('direct_chat_notification', handleDirectChatNotification);
    };
  }, [socket, user]);

  // الدوال الفرعية (بحث، قبول، رفض)
  const handleSearchQueryChange = async (val) => {
    setSearchQuery(val);
    const trimmed = val.trim();
    if (!trimmed) {
      setSuggestions([]);
      setSearchResult(null);
      setSearchError('');
      return;
    }

    // دعم البحث بـ @ أو بدون
    const cleanVal = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    if (cleanVal.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await fetch(`/api/users/search/by-username/${encodeURIComponent(trimmed)}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.users || []);
        setSearchError('');
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error("Suggestions fetch error:", err);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const res = await fetch(`/api/users/search/by-username/${encodeURIComponent(searchQuery.trim())}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResult(data.user);
        setSuggestions(data.users || []);
      } else {
        setSearchResult(null);
        setSuggestions([]);
        setSearchError('لم يتم العثور على مستخدم بهذا الاسم.');
      }
    } catch (err) {
      console.error('Error searching user:', err);
      setSearchError('حدث خطأ أثناء البحث. حاول مرة أخرى.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSendFriendRequest = async (targetUserId) => {
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/friends/${userId}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId })
      });
      if (res.ok) {
        setRequestSentUserIds(prev => new Set([...prev, targetUserId]));
        alert('تم إرسال طلب الصداقة بنجاح!');
        if (socket) {
            socket.emit('send_friend_request');
        }
      } else {
        const data = await res.json();
        alert(data.error || 'فشل في إرسال طلب الصداقة.');
      }
    } catch (err) {
      console.error('Error sending friend request:', err);
      alert('حدث خطأ أثناء إرسال طلب الصداقة.');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('هل أنت متأكد من إزالة هذا الصديق؟')) return;
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/friends/${userId}/remove/${friendId}`, { method: 'POST' });
      if (res.ok) {
        const removed = friends.find(f => f.id === friendId);
        setFriends(prev => prev.filter(f => f.id !== friendId));
        if (removed) {
          setRemovedList(prev => [{
            id: removed.id,
            nickname: removed.alias || removed.firstName || 'مستخدم',
            username: removed.username,
            avatarUrl: removed.avatarUrl,
            removedAt: new Date().toISOString()
          }, ...prev]);
        }
      } else {
        alert('حدث خطأ أثناء الإزالة.');
      }
    } catch (err) {
      console.error('Error removing friend:', err);
    }
    setActiveMenuFriendId(null);
  };

  const handleUnblockFromList = async (friendId) => {
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/friends/${userId}/unblock/${friendId}`, { method: 'POST' });
      if (res.ok) {
        setBlockedList(prev => prev.filter(b => b.id !== friendId));
        // Reload friends list
        const resFriends = await fetch(`/api/friends/${userId}`);
        if (resFriends.ok) {
          const data = await resFriends.json();
          setFriends(sortFriendsByActivity(data.friends.map(f => ({ ...f, isOnline: false, unreadCount: f.unreadCount || 0 }))));
        }
      } else {
        alert('حدث خطأ أثناء إلغاء الحظر.');
      }
    } catch (err) {
      console.error('Error unblocking:', err);
    }
  };

  const handleReAddFriend = async (targetUserId) => {
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/friends/${userId}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId })
      });
      if (res.ok) {
        setRemovedList(prev => prev.filter(r => r.id !== targetUserId));
        alert('تم إرسال طلب الصداقة بنجاح!');
        if (socket) socket.emit('send_friend_request');
      } else {
        const data = await res.json();
        alert(data.error || 'فشل في إرسال طلب الصداقة.');
      }
    } catch (err) {
      console.error('Error re-adding friend:', err);
    }
  };

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'غير متصل';
    const date = new Date(dateString);
    return `آخر ظهور: ${date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleAccept = async (friendshipId) => {
      try {
          const userId = user.id || user.telegramId;
          const res = await fetch(`/api/friends/${userId}/accept`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ friendshipId })
          });
          if (res.ok) {
              setRequests(prev => prev.filter(r => r.friendshipId !== friendshipId));
              if (socket) socket.emit('accept_friend_request'); 
              const resFriends = await fetch(`/api/friends/${userId}`);
              if (resFriends.ok) {
                 const data = await resFriends.json();
                 setFriends(data.friends.map(f => ({ ...f, isOnline: false })));
              }
          }
      } catch (err) {
          console.error(err);
      }
  };

  const handleReject = async (friendshipId) => {
      try {
          const userId = user.id || user.telegramId;
          const res = await fetch(`/api/friends/${userId}/reject`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ friendshipId })
          });
          if (res.ok) {
              setRequests(prev => prev.filter(r => r.friendshipId !== friendshipId));
          }
      } catch (err) {
          console.error(err);
      }
  };

  // 1. حالة التحميل (Skeleton)
  if (loading) {
    return (
      <div className="friends-container">
        <div className="skeleton-tabs"></div>
        <div className="skeleton-item"></div>
        <div className="skeleton-item"></div>
        <div className="skeleton-item"></div>
      </div>
    );
  }

  // 2. عرض المحادثة المباشرة مع صديق (مستقلة بالكامل)
  if (directChatFriend) {
    return (
      <DirectChat
        user={user}
        friend={directChatFriend}
        onBack={() => setDirectChatFriend(null)}
      />
    );
  }

  // 3. عرض قائمة التبويبات والمحتويات الديناميكية
  return (
    <div className="friends-container">
      {/* Segmented Controls للتبديل بين الصفحات الفرعية - تظل ظاهرة دائماً */}
      <div className="friends-tabs-wrapper">
          <div className="friends-tabs">
              <button
                  className={`tab-btn ${view === 'friends' ? 'active' : ''}`}
                  onClick={() => setView('friends')}
              >
                  <Users size={16} />
                  <span>الأصدقاء</span>
              </button>
              <button
                  className={`tab-btn ${view === 'anonymous' ? 'active' : ''}`}
                  onClick={() => {
                      if (!isSubscribed) {
                          if (onRequestSubscription) onRequestSubscription();
                          return;
                      }
                      setView('anonymous');
                  }}
              >
                  <Eye size={16} />
                  <span>مجهول</span>
              </button>
              <button
                  className={`tab-btn ${view === 'mylink' ? 'active' : ''}`}
                  onClick={() => setView('mylink')}
              >
                  <Send size={16} />
                  <span>رابطي</span>
              </button>
          </div>
      </div>

      {/* منطقة عرض المحتوى المتغير بناءً على التبويب النشط */}
      <div className="friends-tab-content" style={{ marginTop: '12px' }}>
          {view === 'friends' && (
             <>
                 {/* Sub-tabs داخل قسم الأصدقاء */}
                 <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '4px' }}>
                     <button
                         onClick={() => setFriendsSubView('list')}
                         style={{
                             flex: 1, padding: '8px 0', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s',
                             background: friendsSubView === 'list' ? 'var(--accent-color, #6366f1)' : 'transparent',
                             color: friendsSubView === 'list' ? '#fff' : 'var(--text-secondary, #888)'
                         }}
                     >
                         <Users size={14} /> الأصدقاء
                     </button>
                     <button
                         onClick={() => setFriendsSubView('blocked')}
                         style={{
                             flex: 1, padding: '8px 0', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s',
                             background: friendsSubView === 'blocked' ? '#ef444420' : 'transparent',
                             color: friendsSubView === 'blocked' ? '#f87171' : 'var(--text-secondary, #888)'
                         }}
                     >
                         <Shield size={14} /> المحظورون {blockedList.length > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', padding: '1px 5px', fontSize: '0.7rem' }}>{blockedList.length}</span>}
                     </button>
                     <button
                         onClick={() => setFriendsSubView('removed')}
                         style={{
                             flex: 1, padding: '8px 0', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s',
                             background: friendsSubView === 'removed' ? 'rgba(148,163,184,0.12)' : 'transparent',
                             color: friendsSubView === 'removed' ? '#94a3b8' : 'var(--text-secondary, #888)'
                         }}
                     >
                         <UserX size={14} /> السابقون {removedList.length > 0 && <span style={{ background: '#64748b', color: '#fff', borderRadius: '50%', padding: '1px 5px', fontSize: '0.7rem' }}>{removedList.length}</span>}
                     </button>
                 </div>

                 {/* محتوى sub-view: قائمة الأصدقاء */}
                 {friendsSubView === 'list' && (<>
                 {/* قسم البحث عن مستخدم بالاسم الفريد */}
                  <div className="friends-search-section">
                      <form onSubmit={handleSearch} className="friends-search-form">
                          <div className="username-input-wrapper" style={{ flex: 1 }}>
                              <AtSign size={18} className="input-icon" />
                              <input 
                                  type="text" 
                                  placeholder="ابحث بالاسم الفريد للمستخدم (مثال: serrak_1)..." 
                                  value={searchQuery}
                                  onChange={(e) => handleSearchQueryChange(e.target.value)}
                                  className="friends-search-input"
                              />
                          </div>
                          <button type="submit" className="friends-search-btn" disabled={searchLoading}>
                              {searchLoading ? 'جاري...' : 'بحث'}
                          </button>
                      </form>

                      {suggestions.length > 0 && (
                          <div className="search-suggestions-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                              {suggestions.map(result => {
                                  const foundFriend = friends.find(f => f.id === result.telegramId || f.id === result.id);
                                  const isAlreadyFriend = !!foundFriend;

                                  return (
                                      <div key={result.telegramId} className="search-result-card" style={isAlreadyFriend ? { border: '1px solid rgba(16, 185, 129, 0.3)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)' } : {}}>
                                          <div className="search-result-info" onClick={() => setViewProfileUserId(result.telegramId)} style={{ cursor: 'pointer' }}>
                                              <div className="search-result-avatar">
                                                  {result.avatarUrl ? (
                                                      <img src={result.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                  ) : (
                                                      <Users size={20} />
                                                  )}
                                              </div>
                                              <div className="search-result-details">
                                                  <span className="search-result-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                      <VipName user={{ nickname: result.nickname || result.firstName, isVip: result.isVip, customNameColor: result.customNameColor }} />
                                                      {isAlreadyFriend && (
                                                          <span className="friend-badge" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontSize: '0.75rem', padding: '3px 8px', borderRadius: '12px', fontWeight: '600' }}>صديق ✨</span>
                                                      )}
                                                  </span>
                                                  <span className="search-result-username">@{result.customUsername}</span>
                                              </div>
                                          </div>
                                          <div className="search-result-actions">
                                              {isAlreadyFriend ? (
                                                  <button className="btn-open-chat" onClick={() => handleOpenChat(foundFriend)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                      <MessageSquare size={16} />
                                                      <span>دردشة</span>
                                                  </button>
                                              ) : requestSentUserIds.has(result.telegramId) ? (
                                                  <span className="request-sent-label">تم الإرسال</span>
                                              ) : (
                                                  <button className="btn-add-friend" onClick={() => handleSendFriendRequest(result.telegramId)}>
                                                      <UserPlus size={16} />
                                                      <span>إضافة صديق</span>
                                                  </button>
                                              )}
                                          </div>
                                      </div>
                                  );
                              })}
                          </div>
                      )}

                     {searchError && (
                         <div className="search-error-message">
                             <span>{searchError}</span>
                         </div>
                     )}
                 </div>

                 {/* قسم طلبات الصداقة المعلقة */}
                 {requests.length > 0 && (
                     <div className="friend-requests-section">
                         <div className="section-header">
                             <UserPlus size={20} style={{ color: '#60a5fa' }} />
                             <h3>طلبات الصداقة ({requests.length})</h3>
                         </div>
                         <div className="requests-list">
                             {requests.map(req => (
                                 <div key={req.friendshipId} className="request-card">
                                     <div className="request-info" onClick={() => setViewProfileUserId(req.requesterId)} style={{ cursor: 'pointer' }}>
                                         <div className="request-avatar">
                                             {req.avatarUrl ? (
                                                 <img src={req.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                             ) : (
                                                 <Users size={20} />
                                             )}
                                         </div>
                                         <div>
                                             <span className="request-name">{req.nickname}</span>
                                             <span className="request-date">أرسل إليك طلب صداقة</span>
                                         </div>
                                     </div>
                                     <div className="request-actions">
                                         <button className="btn-icon accept" onClick={() => handleAccept(req.friendshipId)} aria-label="قبول">
                                             <Check size={18} />
                                         </button>
                                         <button className="btn-icon reject" onClick={() => handleReject(req.friendshipId)} aria-label="رفض">
                                             <X size={18} />
                                         </button>
                                     </div>
                                 </div>
                             ))}
                         </div>
                     </div>
                 )}

                 {/* قائمة الأصدقاء */}
                 <div className="friends-list-section">
                     <h2 className="section-title">قائمة الأصدقاء</h2>
                     {friends.length === 0 ? (
                       <div className="empty-state">
                           <Users size={48} className="empty-icon" />
                           <p>لا يوجد أصدقاء حالياً. ابدأ بالبحث عن أصدقاء جدد!</p>
                       </div>
                     ) : (
                       <div className="friends-list">
                           {friends.map(friend => (
                           <div key={friend.id} className="friend-card">
                               <div className="friend-card-left" onClick={() => setViewProfileUserId(friend.id)} style={{ cursor: 'pointer' }}>
                                   <div className="friend-avatar-wrapper">
                                       <div className="friend-avatar">
                                           {friend.avatarUrl ? (
                                               <img src={friend.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                           ) : (
                                               <Users size={24} />
                                           )}
                                       </div>
                                       <span className={`status-dot ${friend.isOnline ? 'online' : 'offline'}`}></span>
                                   </div>
                                   <div className="friend-info">
                                        <span className="friend-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <VipName user={{ nickname: friend.alias, isVip: friend.isVip, customNameColor: friend.customNameColor }} />
                                            {friend.unreadCount > 0 && (
                                                <span className="friend-unread-badge" style={{
                                                    backgroundColor: '#ef4444',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    padding: '2px 6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    lineHeight: '1',
                                                    minWidth: '16px',
                                                    textAlign: 'center'
                                                }}>
                                                    {friend.unreadCount}
                                                </span>
                                            )}
                                        </span>
                                        <span className={`friend-status ${friend.isOnline ? 'text-online' : 'text-offline'}`}>
                                            {friend.isOnline ? 'متصل الآن' : formatLastSeen(friend.lastSeen)}
                                        </span>
                                    </div>
                               </div>
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <button className="btn-open-chat" onClick={() => handleOpenChat(friend)} aria-label="محادثة" title="محادثة مباشرة">
                                        <MessageSquare size={16} />
                                        <span>دردشة</span>
                                    </button>
                                    {/* زر الخيارات ⋮ */}
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <button
                                            className="btn-friend-menu"
                                            onClick={(e) => { e.stopPropagation(); setActiveMenuFriendId(activeMenuFriendId === friend.id ? null : friend.id); }}
                                            title="خيارات"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', color: 'var(--text-secondary, #888)', display: 'flex', alignItems: 'center' }}
                                        >
                                            <MoreVertical size={18} />
                                        </button>
                                        {activeMenuFriendId === friend.id && (
                                            <div ref={menuRef} className="friend-options-menu">
                                                <button onClick={() => { handleOpenChat(friend); setActiveMenuFriendId(null); }} className="menu-option-btn">
                                                    <MessageSquare size={15} style={{ color: '#38bdf8' }} />
                                                    <span>دردشة مباشرة</span>
                                                </button>
                                                <button onClick={() => handleRemoveFriend(friend.id)} className="menu-option-btn remove-friend">
                                                    <UserMinus size={15} />
                                                    <span>إزالة من الأصدقاء</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                           </div>
                           ))}
                       </div>
                     )}
                 </div>
                 </> /* end friendsSubView === 'list' */)}

                 {/* sub-view: المحظورون */}
                 {friendsSubView === 'blocked' && (
                     <div className="friends-list-section">
                         {blockedList.length === 0 ? (
                             <div className="empty-state">
                                 <ShieldOff size={48} className="empty-icon" style={{ opacity: 0.4 }} />
                                 <p>لا يوجد مستخدمون محظورون.</p>
                             </div>
                         ) : (
                             <div className="friends-list">
                                 {blockedList.map(blocked => (
                                     <div key={blocked.id} className="friend-card">
                                         <div className="friend-card-left">
                                             <div className="friend-avatar-wrapper">
                                                 <div className="friend-avatar" style={{ opacity: 0.6 }}>
                                                     {blocked.avatarUrl ? (
                                                         <img src={blocked.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                     ) : (
                                                         <UserX size={24} />
                                                     )}
                                                 </div>
                                             </div>
                                             <div className="friend-info">
                                                 <span className="friend-name">{blocked.nickname}</span>
                                                 {blocked.username && <span className="friend-status">@{blocked.username}</span>}
                                             </div>
                                         </div>
                                         <button onClick={() => handleUnblockFromList(blocked.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                                             <ShieldOff size={15} />
                                             <span>إلغاء الحظر</span>
                                         </button>
                                     </div>
                                 ))}
                             </div>
                         )}
                     </div>
                 )}

                 {/* sub-view: السابقون */}
                 {friendsSubView === 'removed' && (
                     <div className="friends-list-section">
                         {removedList.length === 0 ? (
                             <div className="empty-state">
                                 <Users size={48} className="empty-icon" style={{ opacity: 0.4 }} />
                                 <p>لا يوجد أصدقاء سابقون.</p>
                             </div>
                         ) : (
                             <div className="friends-list">
                                 {removedList.map(removed => (
                                     <div key={removed.id} className="friend-card">
                                         <div className="friend-card-left">
                                             <div className="friend-avatar-wrapper">
                                                 <div className="friend-avatar" style={{ opacity: 0.6 }}>
                                                     {removed.avatarUrl ? (
                                                         <img src={removed.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                     ) : (
                                                         <Users size={24} />
                                                     )}
                                                 </div>
                                             </div>
                                             <div className="friend-info">
                                                 <span className="friend-name">{removed.nickname}</span>
                                                 {removed.username && <span className="friend-status">@{removed.username}</span>}
                                             </div>
                                         </div>
                                         <button onClick={() => handleReAddFriend(removed.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                                             <RotateCcw size={15} />
                                             <span>إعادة إضافة</span>
                                         </button>
                                     </div>
                                 ))}
                             </div>
                         )}
                     </div>
                 )}
             </>
          )}

          {view === 'anonymous' && (
              <AnonymousChat
                user={user}
                isSubscribed={isSubscribed}
                onRequestSubscription={onRequestSubscription}
                onBack={() => setView('friends')}
              />
          )}

          {view === 'mylink' && (
              <MyLink user={user} />
          )}
      </div>

      {/* Modal عرض الملف الشخصي */}
      {viewProfileUserId && (
        <UserProfile
          userId={viewProfileUserId}
          currentUser={user}
          onClose={() => setViewProfileUserId(null)}
          vipSettings={vipSettings}
        />
      )}
    </div>
  );
};

export default Friends;
